'use client';

import classNames from 'classnames';
import { MaterialIcon } from '../../components/MaterialIcon';
import type { CmsProduct } from '../../types/cmsProduct';
import { useCompareToggle } from './CompareToggle';

export type CompareTogglePillProps = {
  product: CmsProduct | undefined;
  className?: string;
};

/**
 * The credit card's compare control: a pill for the card's top-right corner, clear of the badge
 * band (BRD-02 FR-2.2.1). "Compare" with a two-squares icon; selected, it fills brand blue and reads
 * "Added". At the cap an unselected pill disables and explains the limit in its tooltip, as the row
 * toggle does. Placing it on the card is the card's job; like the row toggle it renders nothing
 * without a `CompareProvider`.
 */
export const CompareTogglePill = ({
  product,
  className,
}: CompareTogglePillProps) => {
  const state = useCompareToggle(product, 'pill');
  if (!state) return null;
  const { id, selected, disabled, staticText, capHint, hintId, label, toggle } =
    state;

  return (
    <button
      type="button"
      aria-pressed={selected}
      aria-disabled={disabled || undefined}
      aria-label={label}
      aria-describedby={disabled ? hintId : undefined}
      title={disabled ? capHint : undefined}
      data-component="compare-toggle-pill"
      data-product-id={id}
      data-selected={selected}
      onClick={toggle}
      className={classNames(
        'mhg-alpha-typography-caption inline-flex h-7 items-center gap-spacing/gap/x-xs rounded-full border px-spacing/gap/x-sm',
        selected
          ? 'border-buttons/primary bg-buttons/primary text-buttons/base'
          : disabled
            ? 'cursor-not-allowed border-borders/default bg-buttons/base text-text/disabled'
            : 'border-borders/form-default bg-buttons/base text-text/primary',
        className,
      )}
    >
      <MaterialIcon icon={selected ? 'check' : 'filter_none'} px={16} />
      {selected ? staticText.compare_added : staticText.compare}
      {disabled && (
        <span id={hintId} className="sr-only">
          {capHint}
        </span>
      )}
    </button>
  );
};
